/* eslint-disable no-undef */
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Avatar, Button, CssBaseline, TextField, Typography, Container } from "@mui/material";
import Alert from "@mui/lab/Alert";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useFormik } from "formik";
import Cookie from "js-cookie";
import { useDispatch, useSelector } from "react-redux";

import Loader from "../../components/shared/SuspenseFallback";
import { verifyQRCode, verifySMSCode, sendSMSCode, useStyles } from "./helper";
import { setUserVerificationAction } from "../../redux/users/actions";

const VerifyToken = () => {
  const dispatch = useDispatch();
  const userInfoCookie = Cookie.getJSON("userInfo");
  const {
    userInfo: { send_sms_2fa_enabled, two_factor_enabled, phone_number, _id, token },
  } = useSelector((state) => state.user);
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [useSMSAuth, setUseSMSAuth] = useState(false);

  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    initialValues: {
      security_code: "",
    },
    onSubmit: async ({ security_code }) => {
      setErrorMsg("");
      setLoading(true);
      if (useSMSAuth === false) {
        const { status, error: catchErrorMsg } = await verifyQRCode(
          userInfoCookie._id,
          security_code,
          token,
        );
        if (status === 200) {
          setLoading(false);
          setErrorMsg("");
          dispatch(setUserVerificationAction(true));
        } else {
          setErrorMsg(catchErrorMsg);
          setLoading(false);
        }
      } else if (useSMSAuth === true) {
        const { status, error: catchErrorMsg } = await verifySMSCode(
          userInfoCookie._id,
          security_code,
          token,
        );
        if (status === 200) {
          setErrorMsg("");
          dispatch(setUserVerificationAction(true));
          const verifiedUserInfo = { ...userInfoCookie, isUserVerified: true };
          Cookies.remove("userInfo");
          Cookies.set("userInfo", verifiedUserInfo);
        } else {
          setErrorMsg(catchErrorMsg);
          setLoading(false);
        }
      }
    },
    validationSchema: Yup.object().shape({
      security_code: Yup.string()
        .matches(new RegExp("^[0-9]{6}$"), "Add valid code")
        .required("Verification code is required"),
    }),
  });

  const sendSMS = async () => {
    setUseSMSAuth(true);
    const { status, error: catchError } = await sendSMSCode(_id, phone_number);
    if (status === 200) {
      setErrorMsg("");
      setLoading(false);
    } else {
      setErrorMsg(catchError);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (send_sms_2fa_enabled === true && two_factor_enabled === false) sendSMS();
  }, [send_sms_2fa_enabled, two_factor_enabled]);

  return (
    <Container component="main" maxWidth="xs">
      {loading && <Loader />}
      <CssBaseline />
      <div className={classes.paper}>
        <Avatar className={classes.avatar}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography className={classes.paragraph}>
          {send_sms_2fa_enabled && two_factor_enabled
            ? "Enter the 6-digit code generated by your authentication app"
            : send_sms_2fa_enabled
              ? " Enter the 6-digit code sent to your number"
              : "Enter the 6-digit code generated by your authentication app"}
        </Typography>
        {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
        <form className={classes.form} onSubmit={formik.handleSubmit} autoComplete="off" noValidate>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="security_code"
            label="Security code"
            name="security_code"
            autoFocus
            onChange={formik.handleChange}
            value={formik.values.security_code || ""}
            error={!!formik.errors.security_code}
            helperText={formik.errors.security_code ? formik.errors.security_code : ""}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
          >
            Confirm
          </Button>
          {send_sms_2fa_enabled === true && two_factor_enabled === true ? (
            <span>
              If you're unable to receive a security code from your authentication app, you can get
              one by
              <span className={classes.textMsgLink} onClick={sendSMS}>
                text message
              </span>
            </span>
          ) : null}
        </form>
      </div>
    </Container>
  );
};

export default VerifyToken;
